/**
 * ReopenCardDialogView — pure presentation for reopening a closed card into a
 * WORKING stage (SE-5496).
 *
 * A closed card can only re-enter a non-terminal stage, so the picker is
 * rendered from the caller-supplied `workingPhases` (the board's phases with
 * `isTerminal === false`) — a terminal stage can never appear here. A reopen
 * rationale is OPTIONAL (the `ReopenKanbanCardInput.rationale` is nullable), so
 * unlike the terminal-close prompt the confirm is gated ONLY on a chosen stage.
 *
 * Built on `DesktopModal` with the same deliberate backdrop/ESC opt-out as the
 * stage-move prompt: a half-filled reopen should not be discarded by a stray
 * click. Fully controlled — every value is a prop, including the per-stage press
 * handlers and the submit gate, which `ReopenCardDialogContainer` resolves.
 * @module features/player-kanban/components/molecules/ReopenCardDialog/ReopenCardDialogView
 */

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";
import { memo } from "react";

import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { Textarea, TextareaInput } from "@/components/atoms/Textarea";
import DesktopModal from "@/components/molecules/DesktopModal";

/** Cap on the reopen rationale, mirroring the stage-move reason convention. */
export const REOPEN_RATIONALE_MAX_LENGTH = 280;

/** A working (non-terminal) stage the card can be reopened into. */
export interface ReopenStageOption {
  /** Phase id. */
  readonly id: string;
  /** Phase display name. */
  readonly name: string;
}

/** Props for {@link ReopenCardDialogView}. */
export interface ReopenCardDialogViewProps {
  /** Whether the dialog is visible. */
  readonly isOpen: boolean;
  /** Player name for the dialog copy, when known. */
  readonly playerName?: string;
  /** Working stages the card may be reopened into. */
  readonly workingStages: readonly ReopenStageOption[];
  /** Currently-selected working stage id, if any. */
  readonly selectedStageId?: string;
  /**
   * Press handler per stage id, pre-bound by the Container so the JSX passes a
   * plain reference (react-perf/jsx-no-new-function-as-prop forbids inline
   * closures in attributes).
   */
  readonly stageHandlers: Record<string, () => void>;
  /** Current optional rationale text. */
  readonly rationale: string;
  /** Rationale change handler. */
  readonly onChangeRationale: (value: string) => void;
  /** Whether a reopen is in flight. */
  readonly isSubmitting: boolean;
  /** Whether the confirm is enabled — a working stage must be chosen. */
  readonly canSubmit: boolean;
  /** Confirm handler. */
  readonly onSubmit: () => void;
  /** Cancel handler. */
  readonly onCancel: () => void;
}

/**
 * Renders the reopen-into-working-stage dialog.
 * @param props - {@link ReopenCardDialogViewProps}
 * @param props.isOpen - Whether the dialog is visible
 * @param props.playerName - Player name for the copy
 * @param props.workingStages - Working stages to choose from
 * @param props.selectedStageId - Currently-selected stage id
 * @param props.stageHandlers - Pre-bound press handler per stage id
 * @param props.rationale - Current optional rationale text
 * @param props.onChangeRationale - Rationale change handler
 * @param props.isSubmitting - Whether a reopen is in flight
 * @param props.canSubmit - Whether the confirm is enabled
 * @param props.onSubmit - Confirm handler
 * @param props.onCancel - Cancel handler
 * @returns The dialog modal, or null when closed.
 */
const ReopenCardDialogView = ({
  isOpen,
  playerName,
  workingStages,
  selectedStageId,
  stageHandlers,
  rationale,
  onChangeRationale,
  isSubmitting,
  canSubmit,
  onSubmit,
  onCancel,
}: ReopenCardDialogViewProps) =>
  isOpen ? (
    <DesktopModal
      isOpen
      onClose={onCancel}
      title="Reopen card"
      width="md"
      closeOnBackdrop={false}
      closeOnEsc={false}
      footer={
        <Stack direction="horizontal" className="w-full justify-end gap-2">
          <Button
            testID={TEST_ID_PLAYER_KANBAN.REOPEN_DIALOG_CANCEL}
            variant="outline"
            tone="secondary"
            onPress={onCancel}
            isDisabled={isSubmitting}
            label="Cancel"
          />
          <Button
            testID={TEST_ID_PLAYER_KANBAN.REOPEN_DIALOG_SUBMIT}
            tone="primary"
            onPress={onSubmit}
            isDisabled={!canSubmit}
            isLoading={isSubmitting}
            label="Reopen"
          />
        </Stack>
      }
    >
      <Stack
        className="w-full gap-3"
        testID={TEST_ID_PLAYER_KANBAN.REOPEN_DIALOG}
        {...{ "data-testid": TEST_ID_PLAYER_KANBAN.REOPEN_DIALOG }}
      >
        <Text variant="body" className="text-content-secondary">
          {playerName
            ? `Reopen ${playerName} into a working stage.`
            : "Reopen this card into a working stage."}
        </Text>
        <Stack className="w-full gap-2">
          <Text variant="body-strong" className="text-content-primary">
            Working stage
          </Text>
          <Stack direction="horizontal" className="w-full flex-wrap gap-2">
            {workingStages.map(stage => (
              <Button
                key={stage.id}
                testID={TEST_ID_PLAYER_KANBAN.reopenStageOptionFor(stage.id)}
                variant={selectedStageId === stage.id ? "solid" : "outline"}
                tone={selectedStageId === stage.id ? "primary" : "secondary"}
                size="sm"
                onPress={stageHandlers[stage.id]}
                label={stage.name}
              />
            ))}
          </Stack>
        </Stack>
        <Textarea size="md" className="min-h-24">
          <TextareaInput
            accessibilityLabel="Reopen reason (optional)"
            value={rationale}
            onChangeText={onChangeRationale}
            placeholder="Add an optional note (recorded on the activity log)"
            maxLength={REOPEN_RATIONALE_MAX_LENGTH}
          />
        </Textarea>
      </Stack>
    </DesktopModal>
  ) : null;

ReopenCardDialogView.displayName = "ReopenCardDialogView";

export default memo(ReopenCardDialogView);
